import {exhaustive} from '@freckle/exhaustive'
import {maybe} from '@freckle/maybe'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {
  parseLinkHeader,
  parseOperationLocationHeader
} from '@freckle/educator-entities/ts/common/helpers/ajax'
import type {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import type {TeacherRole} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import {
  fancyClient,
  ensureOk,
  extractDataAndHeaders
} from '@freckle/educator-entities/ts/openapi-clients'
import {
  providersOrNoneToQueryFilter,
  NO_IDENTITY_PROVIDER_VALUE
} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TeacherTabFilteringT} from '@freckle/school/ts/manage-teachers/components/teacher-tab'
import type {TeacherTableRowT} from '@freckle/school/ts/manage-teachers/models/teacher-table-row'
import {parseTeacherTableRows} from '@freckle/school/ts/manage-teachers/models/teacher-table-row'

type RemoveAffiliationsT =
  | {
      tag: 'non-idp-teachers'
      schoolIds: Array<number>
    }
  | {
      tag: 'teachers.id'
      teacherIds: Array<number>
    }

export const nonIdpTeachersFilter: TeacherTabFilteringT = {
  tag: 'teacher-filters',
  identityProviders: [NO_IDENTITY_PROVIDER_VALUE]
}

export function fetchTeacherTableRows(
  schoolIds: Array<number>,
  tableParams: TableParamsT<TeacherTabFilteringT>
): Promise<{
  teacherTableRows: Array<TeacherTableRowT>
  links: LinksT
  totalCount: number
}> {
  const {additionalFilters} = tableParams

  const studentGradeFilterParam = maybe(
    () => ({}),
    filters =>
      maybe(
        () => ({}),
        grade => ({'students.grade': grade}),
        filters.studentGrade
      ),
    additionalFilters
  )

  const identityProviderFilterParam = providersOrNoneToQueryFilter(
    additionalFilters?.identityProviders ?? []
  )

  const query = {
    'school.id': schoolIds,
    position: tableParams.position ?? undefined,
    limit: tableParams.limit ?? undefined,
    'courses.id': additionalFilters?.course?.id,
    'students.id': additionalFilters?.student?.id,
    role: additionalFilters?.role as TeacherRole | undefined,
    name: additionalFilters?.name,
    'school.name': additionalFilters?.school?.name,
    email: additionalFilters?.email,
    ...studentGradeFilterParam,
    ...identityProviderFilterParam
  }

  return fancyClient
    .GET('/3/teachers', {
      params: {query}
    })
    .then(extractDataAndHeaders)
    .then(({data, headers}) => {
      const linkHeader = headers.get('Link')
      const links = parseLinkHeader(linkHeader)

      const totalCountHeader = headers.get('X-Total-Count')
      const totalCount = totalCountHeader !== null ? parseInt(totalCountHeader, 10) : 0

      const teacherTableRows = parseTeacherTableRows(data)
      return {
        teacherTableRows,
        links,
        totalCount
      }
    })
}

export async function removeAffiliation(teacherId: number): Promise<void> {
  await fancyClient
    .DELETE('/2/teachers/{TeachersAffiliationR-TeacherId}/affiliation', {
      params: {
        path: {
          'TeachersAffiliationR-TeacherId': teacherId
        }
      }
    })
    .then(ensureOk)
}

type BulkUnaffiliationOperationT = {
  operationLocation: OperationLocationT
  teacherIds: Array<number>
}

export async function removeAffiliations(
  removeAffiliations: RemoveAffiliationsT
): Promise<BulkUnaffiliationOperationT> {
  return fancyClient
    .DELETE('/2/teachers/affiliations', {
      params: {
        query: getRemoveAffiliationQuery(removeAffiliations)
      }
    })
    .then(extractDataAndHeaders)
    .then(({data, headers}) => {
      const operationLocationHeader = headers.get('Operation-Location')
      const operationLocation = parseOperationLocationHeader(operationLocationHeader)
      return {
        operationLocation,
        teacherIds: data.teacherIds
      }
    })
}

function getRemoveAffiliationQuery(
  removeAffiliations: RemoveAffiliationsT
): Record<string, Array<number>> {
  switch (removeAffiliations.tag) {
    case 'non-idp-teachers':
      return {[removeAffiliations.tag]: removeAffiliations.schoolIds}
    case 'teachers.id':
      return {[removeAffiliations.tag]: removeAffiliations.teacherIds}
    default:
      return exhaustive((removeAffiliations as {tag: never}).tag)
  }
}
